import { useCallback, useEffect, useState } from 'react';

/**
 * Theme.
 *
 * Three states, not two: Light, Dark, and System. System keeps tracking the OS after
 * the page has loaded, which a one-shot media query read at startup would not do.
 * The initial class is applied by an inline script before first paint; this hook only
 * keeps it in sync afterwards.
 */

export type ThemeMode = 'light' | 'dark' | 'system';
const STORAGE_KEY = 'atlas.theme';

function readStored(): ThemeMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system';
  } catch {
    // A browser with storage disabled still gets a working, consistent theme.
    return 'system';
  }
}

function apply(mode: ThemeMode): void {
  const dark =
    mode === 'dark' ||
    (mode === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  document.documentElement.dataset['theme'] = dark ? 'dark' : 'light';
  document.documentElement.classList.toggle('theme-dark', dark);
  document.body.classList.toggle('theme-dark', dark);
  document.body.classList.toggle('theme-light', !dark);
}

export function useTheme() {
  const [mode, setMode] = useState<ThemeMode>(readStored);

  useEffect(() => {
    apply(mode);
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Persisting is a convenience; the session still renders correctly without it.
    }
  }, [mode]);

  useEffect(() => {
    if (mode !== 'system') return;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = () => apply('system');
    query.addEventListener('change', listener);
    return () => query.removeEventListener('change', listener);
  }, [mode]);

  return { mode, setMode: useCallback((next: ThemeMode) => setMode(next), []) };
}
